import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';

import VM from 'scratch-vm';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import CommunityButton from './community-button.jsx';
import ShareButton from './share-button.jsx';
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import ProjectWatcher from '../../containers/project-watcher.jsx';
import MenuBarMenu from './menu-bar-menu.jsx';
import MenuLabel from './tw-menu-label.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import ProjectTitleInput from './project-title-input.jsx';
import AuthorInfo from './author-info.jsx';
import SB3Downloader from '../../containers/sb3-downloader.jsx';
import DeletionRestorer from '../../containers/deletion-restorer.jsx';
import TurboMode from '../../containers/turbo-mode.jsx';
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import SettingsMenu from './settings-menu.jsx';
import AccountNav from '../../containers/account-nav.jsx';
import Spinner from '../spinner/spinner.jsx';
import CollaboratorsModal from '../dash-collaborators/collaborators-modal.jsx';

import FramerateChanger from '../../containers/tw-framerate-changer.jsx';
import ChangeUsername from '../../containers/tw-change-username.jsx';
import CloudVariablesToggler from '../../containers/tw-cloud-toggler.jsx';
import TWSaveStatus from './tw-save-status.jsx';
import TWNews from './tw-news.jsx';
import {isNewYearMode} from '../../components/dash-new-year-mode/new-year-mode.jsx';
import getSession, {requestDashApi} from '../../lib/dash-api.js';

import {openTipsLibrary, openSettingsModal, openRestorePointModal} from '../../reducers/modals';
import {setPlayer} from '../../reducers/mode';
import {setSession} from '../../reducers/dash';
import {
    isTimeTravel220022BC,
    isTimeTravel1920,
    isTimeTravel1990,
    isTimeTravel2020,
    isTimeTravelNow,
    setTimeTravel
} from '../../reducers/time-travel';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    manualUpdateProject,
    requestNewProject,
    saveProjectAsCopy
} from '../../reducers/project-state';
import {
    openAboutMenu,
    closeAboutMenu,
    aboutMenuOpen,
    openAccountMenu,
    closeAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    fileMenuOpen,
    openEditMenu,
    closeEditMenu,
    editMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen,
    openModeMenu,
    closeModeMenu,
    modeMenuOpen,
    settingsMenuOpen,
    openSettingsMenu,
    closeSettingsMenu,
    errorsMenuOpen,
    openErrorsMenu,
    closeErrorsMenu
} from '../../reducers/menus';
import {setFileHandle} from '../../reducers/tw.js';

import collectMetadata from '../../lib/collect-metadata';

import * as styles from './menu-bar.css';

import messagesIcon from './icon--messages.png';
import mystuffIcon from './icon--mystuff.png';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import aboutIcon from './icon--about.svg';
import fileIcon from './icon--file.svg';
import editIcon from './icon--edit.svg';
import addonsIcon from './addons.svg';
import errorIcon from './tw-error.svg';
import advancedIcon from './tw-advanced.svg';
import dashLogo from './dash.png';
import dashNewYearLogo from './dash-new-year.png';
// eslint-disable-next-line no-unused-vars
import dashZipprCollabLogo from './dash-zippr-collab-logo.svg';
import searchIcon from './icon--search.png';

import ninetiesLogo from './nineties_logo.svg';
import catLogo from './cat_logo.svg';
import prehistoricLogo from './prehistoric-logo.svg';
import oldtimeyLogo from './oldtimey-logo.svg';

import sharedMessages from '../../lib/shared-messages';

import SeeInsideButton from './tw-see-inside.jsx';
import isScratchDesktop, {notScratchDesktop} from '../../lib/isScratchDesktop.js';
import {APP_NAME} from '../../lib/brand.js';

const twMessages = defineMessages({
    compileError: {
        id: 'tw.menuBar.compileError',
        defaultMessage: '{sprite}: {error}',
        description: 'Error message in error menu'
    }
});

const dashMessages = defineMessages({
    searchPlaceholder: {
        id: 'dash.menuBar.searchPlaceholder',
        defaultMessage: 'Search',
        description: 'Placeholder text for the search field in the menu bar'
    },
    shareProjectWarning: {
        id: 'dash.menuBar.shareProjectWarning',
        defaultMessage: 'Are you sure you want to share project?',
        description: 'Warning message when user clicks share button'
    },
    liveCollaborationUnavailable: {
        id: 'dash.menuBar.liveCollaborationUnavailableMessage',
        defaultMessage: 'Cannot use live-collaboration, most likely because you didn\'t shared the project yet.',
        description: 'Warning message when user clicks live-collaboration unavailable button'
    }
});

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom'
}) => {
    if (enable) {
        return (
            <React.Fragment>
                {children}
            </React.Fragment>
        );
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};


MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
};

const MenuItemTooltip = ({id, isRtl, children, className}) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool
};

const AboutButton = props => (
    <Button
        className={classNames(styles.menuBarItem, styles.hoverable)}
        iconClassName={styles.aboutIcon}
        iconSrc={aboutIcon}
        onClick={props.onClick}
    />
);

AboutButton.propTypes = {
    onClick: PropTypes.func.isRequired
};

// Unlike <MenuItem href="">, this uses an actual <a>
const MenuItemLink = props => (
    <a
        href={props.href}
        rel="noreferrer"
        target="_blank"
        className={styles.menuItemLink}
    >
        <MenuItem>{props.children}</MenuItem>
    </a>
);

MenuItemLink.propTypes = {
    children: PropTypes.node.isRequired,
    href: PropTypes.string.isRequired
};

class MenuBar extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickSeeInside',
            'handleClickNew',
            'handleClickNewWindow',
            'handleClickSave',
            'handleClickSaveAsCopy',
            'handleClickPackager',
            'handleClickDesktopSettings',
            'handleClickRestorePoints',
            'handleOpenCollaborators',
            'handleCloseCollaborators',
            'handleClickSeeCommunity',
            'handleClickShare',
            'handleClickUpdate',
            'handleClickLogOut',
            'handleSetMode',
            'handleKeyPress',
            'handleSearchSubmit',
            'handleRestoreOption',
            'getSaveToComputerHandler',
            'restoreOptionMessage'
        ]);
        this.state = {
            isSharing: false,
            collaboratorsProjectId: null
        };
    }
    componentDidMount () {
        document.addEventListener('keydown', this.handleKeyPress);
    }
    componentWillUnmount () {
        document.removeEventListener('keydown', this.handleKeyPress);
    }
    handleClickNew () {
        // if the project is dirty, and user owns the project, we will autosave.
        // but if they are not logged in and can't save, user should consider
        // downloading or logging in first.
        // Note that if user is logged in and editing someone else's project,
        // they'll lose their work.
        const readyToReplaceProject = this.props.confirmReadyToReplaceProject(
            this.props.intl.formatMessage(sharedMessages.replaceProjectWarning)
        );
        this.props.onRequestCloseFile();
        if (readyToReplaceProject) {
            this.props.onClickNew(this.props.canSave && this.props.canCreateNew);
        }
        this.props.onRequestCloseFile();
    }
    handleClickNewWindow () {
        this.props.onClickNewWindow();
        this.props.onRequestCloseFile();
    }
    handleClickSave () {
        if (this.props.vm.dashCollaboration) return;
        this.props.onClickSave();
        this.props.onRequestCloseFile();
    }
    handleClickSaveAsCopy () {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
    }
    handleOpenCollaborators () {
        this.props.onRequestCloseFile();
        this.setState({collaboratorsProjectId: this.props.projectId});
    }
    handleCloseCollaborators () {
        this.setState({collaboratorsProjectId: null});
    }
    handleClickPackager () {
        this.props.onClickPackager();
        this.props.onRequestCloseFile();
    }
    handleClickDesktopSettings () {
        this.props.onClickDesktopSettings();
        this.props.onRequestCloseSettings();
    }
    handleClickRestorePoints () {
        this.props.onClickRestorePoints();
        this.props.onRequestCloseFile();
    }
    handleClickSeeCommunity (waitForUpdate) {
        if (this.props.shouldSaveBeforeTransition()) {
            this.props.autoUpdateProject(); // save before transitioning to project page
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    async handleClickShare (isFork) {
        if ((!this.props.isShared || isFork) && !this.state.isSharing) {
            if (this.props.canShare) { // save before transitioning to project page
                const session = await getSession();
                if (session && session.id) {
                    // eslint-disable-next-line no-alert
                    if (!confirm(this.props.intl.formatMessage(dashMessages.shareProjectWarning))) {
                        return;
                    }
                    this.setState({
                        isSharing: true
                    });
                    try {
                        let formData = new FormData();
                        const content = await this.props.vm.saveProjectSb3();
                        const fileBlob = new Blob([content], {type: 'application/x.dash.dbp'});
                        formData.append('file', fileBlob, `${this.props.projectTitle}.dbp`);
                        formData.append('name', this.props.projectTitle);
                        if (this.props.projectId && this.props.projectId !== '0') {
                            formData.append('parentId', this.props.projectId);
                        }

                        const response = await requestDashApi('/save-project', {
                            method: 'POST',
                            body: formData,
                            credentials: 'include'
                        });
                        const result = await response.json();
                        if (result.ok) {
                            formData = new FormData();
                            this.props.vm.postIOData('video', {forceTransparentPreview: true});
                            const thumbnailBlob = await new Promise(resolve => {
                                this.props.vm.renderer.requestSnapshot(async dataURI => {
                                    this.props.vm.postIOData('video', {forceTransparentPreview: false});
                                    const res = await fetch(dataURI);
                                    const blob = await res.blob();
                                    resolve(blob);
                                });
                            });
                            formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
                            const thumbnailResponse = await requestDashApi(
                                `/projects/${result.projectId}/upload-thumbnail`,
                                {
                                    method: 'POST',
                                    body: formData,
                                    credentials: 'include'
                                }
                            );
                            if (thumbnailResponse.ok) {
                                // eslint-disable-next-line no-alert
                                alert('Project shared successfully!');
                            } else {
                                // eslint-disable-next-line no-alert
                                alert('Project was shared but thumbnail upload failed');
                            }
                            window.open(`./#${result.projectId}`, '_self');
                        } else {
                            // eslint-disable-next-line no-alert
                            alert(result.error);
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-alert
                        alert(error?.message || error);
                    } finally {
                        this.setState({
                            isSharing: false
                        });
                    }
                    return;
                }
                window.open('./login', '_blank');
                return;
            }
        }
    }
    async handleClickUpdate () {
        if (this.props.vm.dashCollaboration) return;
        if (this.props.isShared && !this.state.isSharing) {
            if (this.props.canShare) { // save before transitioning to project page
                const session = await getSession();
                if (session && session.id) {
                    // eslint-disable-next-line no-alert
                    if (!confirm(this.props.intl.formatMessage(dashMessages.shareProjectWarning))) {
                        return;
                    }
                    this.setState({
                        isSharing: true
                    });
                    try {
                        let formData = new FormData();
                        const content = await this.props.vm.saveProjectSb3();
                        if (this.props.vm.dashCollaboration) return;
                        const fileBlob = new Blob([content], {type: 'application/x.dash.dbp'});
                        formData.append('file', fileBlob, `${this.props.projectTitle}.dbp`);
                        formData.append('name', this.props.projectTitle);

                        const response = await requestDashApi(`/projects/${this.props.projectId}`, {
                            method: 'PUT',
                            body: formData,
                            credentials: 'include'
                        });
                        const result = await response.json();
                        if (result.ok) {
                            formData = new FormData();
                            this.props.vm.postIOData('video', {forceTransparentPreview: true});
                            const thumbnailBlob = await new Promise(resolve => {
                                this.props.vm.renderer.requestSnapshot(async dataURI => {
                                    this.props.vm.postIOData('video', {forceTransparentPreview: false});
                                    const res = await fetch(dataURI);
                                    const blob = await res.blob();
                                    resolve(blob);
                                });
                            });
                            formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
                            const thumbnailResponse = await requestDashApi(
                                `/projects/${this.props.projectId}/upload-thumbnail`,
                                {
                                    method: 'POST',
                                    body: formData,
                                    credentials: 'include'
                                }
                            );
                            if (thumbnailResponse.ok) {
                                // eslint-disable-next-line no-alert
                                alert('Project updated successfully!');
                            } else {
                                // eslint-disable-next-line no-alert
                                alert('Project was updated but thumbnail upload failed');
                            }
                        } else {
                            // eslint-disable-next-line no-alert
                            alert(result.error);
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-alert
                        alert(error?.message || error);
                    } finally {
                        this.setState({
                            isSharing: false
                        });
                    }
                    return;
                }
                window.open('./login', '_blank');
                return;
            }
        }
    }
    async handleClickLogOut () {
        try {
            const response = await requestDashApi('/auth/logout', {credentials: 'include'});
            const data = await response.json();
            // eslint-disable-next-line no-alert
            if (!data.ok) return alert('Sign out failed');
            this.props.setSession({});
            window.location.reload();
        } catch (error) {
            console.warn(error?.message || error);
            // eslint-disable-next-line no-alert
            alert('Sign out failed');
        }
    }
    handleSetMode (mode) {
        return () => {
            // Turn on/off filters for modes.
            if (mode === '1920') {
                document.documentElement.style.filter = 'brightness(.9)contrast(.8)sepia(1.0)';
                document.documentElement.style.height = '100%';
            } else if (mode === '1990') {
                document.documentElement.style.filter = 'hue-rotate(40deg)';
                document.documentElement.style.height = '100%';
            } else {
                document.documentElement.style.filter = '';
                document.documentElement.style.height = '';
            }

            // Change logo for modes
            if (mode === '1990') {
                document.getElementById('logo_img').src = ninetiesLogo;
            } else if (mode === '2020') {
                document.getElementById('logo_img').src = catLogo;
            } else if (mode === '1920') {
                document.getElementById('logo_img').src = oldtimeyLogo;
            } else if (mode === '220022BC') {
                document.getElementById('logo_img').src = prehistoricLogo;
            } else {
                // document.getElementById('logo_img').src = this.props.logo;
            }

            this.props.onSetTimeTravelMode(mode);
        };
    }
    handleRestoreOption (restoreFun) {
        return () => {
            restoreFun();
            this.props.onRequestCloseEdit();
        };
    }
    handleKeyPress (event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier) {
            if (event.key.toLowerCase() === 's') {
                this.props.handleSaveProject();
                event.preventDefault();
            } else if (event.key.toLowerCase() === 'o') {
                event.preventDefault();
                this.props.onStartSelectingFileUpload();
            }
        }
    }
    handleSearchSubmit (e) {
        e.preventDefault();
        const query = e.currentTarget.querySelector('input')?.value?.trim();
        if (!query) return;
        const encodedQuery = encodeURIComponent(query);
        window.open(`search?q=${encodedQuery}`, '_blank');
    }
    getSaveToComputerHandler (downloadProjectCallback) {
        return () => {
            this.props.onRequestCloseFile();
            downloadProjectCallback();
            if (this.props.onProjectTelemetryEvent) {
                const metadata = collectMetadata(this.props.vm, this.props.projectTitle, this.props.locale);
                this.props.onProjectTelemetryEvent('projectDidSave', metadata);
            }
        };
    }
    restoreOptionMessage (deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (<FormattedMessage
                defaultMessage="Restore Sprite"
                description="Menu bar item for restoring the last deleted sprite."
                id="gui.menuBar.restoreSprite"
            />);
        case 'Sound':
            return (<FormattedMessage
                defaultMessage="Restore Sound"
                description="Menu bar item for restoring the last deleted sound."
                id="gui.menuBar.restoreSound"
            />);
        case 'Costume':
            return (<FormattedMessage
                defaultMessage="Restore Costume"
                description="Menu bar item for restoring the last deleted costume."
                id="gui.menuBar.restoreCostume"
            />);
        default: {
            return (<FormattedMessage
                defaultMessage="Restore"
                description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line max-len */
                id="gui.menuBar.restore"
            />);
        }
        }
    }
    handleClickSeeInside () {
        this.props.onClickSeeInside();
    }
    buildAboutMenu (onClickAbout) {
        if (!onClickAbout) {
            // hide the button
            return null;
        }
        if (typeof onClickAbout === 'function') {
            // make a button which calls a function
            return <AboutButton onClick={onClickAbout} />;
        }
        // assume it's an array of objects
        // each item must have a 'title' FormattedMessage and a 'handleClick' function
        // generate a menu with items for each object in the array
        return (
            <MenuLabel
                open={this.props.aboutMenuOpen}
                onOpen={this.props.onRequestOpenAbout}
                onClose={this.props.onRequestCloseAbout}
            >
                <img
                    className={styles.aboutIcon}
                    src={aboutIcon}
                    draggable={false}
                />
                <MenuBarMenu
                    className={classNames(styles.menuBarMenu)}
                    open={this.props.aboutMenuOpen}
                    place={this.props.isRtl ? 'right' : 'left'}
                >
                    {
                        onClickAbout.map(itemProps => (
                            <MenuItem
                                key={itemProps.title}
                                isRtl={this.props.isRtl}
                                onClick={this.wrapAboutMenuCallback(itemProps.onClick)}
                            >
                                {itemProps.title}
                            </MenuItem>
                        ))
                    }
                </MenuBarMenu>
            </MenuLabel>
        );
    }
    wrapAboutMenuCallback (callback) {
        return () => {
            callback();
            this.props.onRequestCloseAbout();
        };
    }
    render () {
        const saveNowMessage = (
            <FormattedMessage
                defaultMessage="Save now"
                description="Menu bar item for saving now"
                id="gui.menuBar.saveNow"
            />
        );
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Fork"
                description="Menu bar item for forking"
                id="dash.menuBar.fork"
            />
        );
        const newProjectMessage = (
            <FormattedMessage
                defaultMessage="New"
                description="Menu bar item for creating a new project"
                id="gui.menuBar.new"
            />
        );
        const searchPlaceholder = this.props.intl.formatMessage(dashMessages.searchPlaceholder);
        const searchValue = new URLSearchParams(window.location.search).get('q');
        const remixButton = (
            <Button
                className={classNames(
                    styles.menuBarButton,
                    styles.remixButton,
                    {[styles.remixButtonIsDisabled]: this.state.isSharing}
                )}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => this.handleClickShare(true)}
            >
                {this.state.isSharing ? (
                    <Spinner
                        className={styles.remixSpinner}
                        small
                    />
                ) : remixMessage}
            </Button>
        );
        // Show the About button only if we have a handler for it (like in the desktop app)
        const aboutButton = this.buildAboutMenu(this.props.onClickAbout);
        const menuBar = (
            <Box
                className={classNames(
                    this.props.className,
                    styles.menuBar,
                    {[styles.centered]: this.props.isPlayerOnly}
                )}
            >
                {this.props.isPlayerOnly && (
                    <div className={styles.settingsMenu}>
                        <div className={styles.settingsGroup}>
                            {(this.props.canChangeTheme || this.props.canChangeLanguage) && <SettingsMenu
                                className={styles.settingsGroup}
                                canChangeLanguage={this.props.canChangeLanguage}
                                canChangeTheme={this.props.canChangeTheme}
                                isRtl={this.props.isRtl}
                                onClickDesktopSettings={
                                    this.props.onClickDesktopSettings &&
                                    this.handleClickDesktopSettings
                                }
                                // eslint-disable-next-line react/jsx-no-bind
                                onOpenCustomSettings={
                                    this.props.onClickAddonSettings &&
                                    this.props.onClickAddonSettings.bind(null, 'editor-theme3')
                                }
                                onRequestClose={this.props.onRequestCloseSettings}
                                onRequestOpen={this.props.onClickSettings}
                                settingsMenuOpen={this.props.settingsMenuOpen}
                            />}
                        </div>
                    </div>
                )}
                <div className={styles.mainMenu}>
                    {this.props.isPlayerOnly && (
                        <a
                            href="/"
                            rel="noreferrer"
                            target="_blank"
                        >
                            <img
                                className={styles.dashLogo}
                                src={isNewYearMode() ? dashNewYearLogo : dashLogo}
                                draggable={false}
                            />
                        </a>
                    )}
                    {this.props.isPlayerOnly && <Divider className={styles.divider} />}
                    {this.props.isPlayerOnly && (
                        <form
                            className={styles.menuBarSearch}
                            onSubmit={this.handleSearchSubmit}
                        >
                            <input
                                type="search"
                                className={styles.menuBarSearchInput}
                                placeholder={searchPlaceholder}
                                aria-label={searchPlaceholder}
                                defaultValue={searchValue}
                            />
                            <button
                                type="submit"
                                className={styles.menuBarSearchButton}
                                aria-label={searchPlaceholder}
                            >
                                <img
                                    className={styles.menuBarSearchIcon}
                                    src={searchIcon}
                                    alt=""
                                />
                            </button>
                        </form>
                    )}
                    <div className={styles.fileGroup}>
                        {!this.props.isPlayerOnly && (
                            this.props.canChangeTheme || this.props.canChangeLanguage
                        ) && (<SettingsMenu
                            className={styles.fileGroup}
                            canChangeLanguage={this.props.canChangeLanguage}
                            canChangeTheme={this.props.canChangeTheme}
                            isRtl={this.props.isRtl}
                            onClickDesktopSettings={
                                this.props.onClickDesktopSettings &&
                                this.handleClickDesktopSettings
                            }
                            // eslint-disable-next-line react/jsx-no-bind
                            onOpenCustomSettings={
                                this.props.onClickAddonSettings &&
                                this.props.onClickAddonSettings.bind(null, 'editor-theme3')
                            }
                            onRequestClose={this.props.onRequestCloseSettings}
                            onRequestOpen={this.props.onClickSettings}
                            settingsMenuOpen={this.props.settingsMenuOpen}
                        />)}
                        {this.props.errors.length > 0 && <div>
                            <MenuLabel
                                open={this.props.errorsMenuOpen}
                                onOpen={this.props.onClickErrors}
                                onClose={this.props.onRequestCloseErrors}
                            >
                                <img
                                    src={errorIcon}
                                    draggable={false}
                                    width={20}
                                    height={20}
                                />
                                <img
                                    src={dropdownCaret}
                                    draggable={false}
                                    width={8}
                                    height={5}
                                />
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.errorsMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                >
                                    <MenuSection>
                                        <MenuItemLink href="https://scratch.mit.edu/users/damir2809/#comments">
                                            <FormattedMessage
                                                defaultMessage="Some scripts encountered errors."
                                                description="Link in error menu"
                                                id="tw.menuBar.reportError1"
                                            />
                                        </MenuItemLink>
                                        <MenuItemLink href="https://scratch.mit.edu/users/damir2809/#comments">
                                            <FormattedMessage
                                                defaultMessage="This is a bug. Please report it."
                                                description="Link in error menu"
                                                id="tw.menuBar.reportError2"
                                            />
                                        </MenuItemLink>
                                    </MenuSection>
                                    <MenuSection>
                                        {this.props.errors.map(({id, sprite, error}) => (
                                            <MenuItem key={id}>
                                                {this.props.intl.formatMessage(twMessages.compileError, {
                                                    sprite,
                                                    error
                                                })}
                                            </MenuItem>
                                        ))}
                                    </MenuSection>
                                </MenuBarMenu>
                            </MenuLabel>
                        </div>}
                        {this.props.isPlayerOnly && <div className={styles.menuBarItem}>
                            <a
                                className={styles.feedbackLink}
                                href="https://scratch.mit.edu/discuss/topic/879252/"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {/* todo: icon */}
                                <Button className={styles.feedbackButton}>
                                    <FormattedMessage
                                        defaultMessage="{APP_NAME} Forum"
                                        description="Button to give link to forum in the menu bar"
                                        id="dash.forumButton"
                                        values={{
                                            APP_NAME
                                        }}
                                    />
                                </Button>
                            </a>
                        </div>}
                        {!this.props.isPlayerOnly && (this.props.canManageFiles) && (
                            <MenuLabel
                                open={this.props.fileMenuOpen}
                                onOpen={this.props.onClickFile}
                                onClose={this.props.onRequestCloseFile}
                            >
                                <img
                                    src={fileIcon}
                                    draggable={false}
                                    width={20}
                                    height={20}
                                />
                                <span className={styles.collapsibleLabel}>
                                    <FormattedMessage
                                        defaultMessage="File"
                                        description="Text for file dropdown menu"
                                        id="gui.menuBar.file"
                                    />
                                </span>
                                <img
                                    src={dropdownCaret}
                                    draggable={false}
                                    width={8}
                                    height={5}
                                />
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.fileMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                >
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClickNew}
                                    >
                                        {newProjectMessage}
                                    </MenuItem>
                                    {this.props.onClickNewWindow && (
                                        <MenuItem
                                            isRtl={this.props.isRtl}
                                            onClick={this.handleClickNewWindow}
                                        >
                                            <FormattedMessage
                                                defaultMessage="New window"
                                                // eslint-disable-next-line max-len
                                                description="Part of desktop app. Menu bar item that creates a new window."
                                                id="tw.menuBar.newWindow"
                                            />
                                        </MenuItem>
                                    )}
                                    {(this.props.canSave || this.props.canCreateCopy) && (
                                        <MenuSection>
                                            {this.props.canSave && !this.props.vm.dashCollaboration && (
                                                <MenuItem onClick={this.handleClickSave}>
                                                    {saveNowMessage}
                                                </MenuItem>
                                            )}
                                            {this.props.canCreateCopy && (
                                                <MenuItem onClick={this.handleClickSaveAsCopy}>
                                                    {createCopyMessage}
                                                </MenuItem>
                                            )}
                                        </MenuSection>
                                    )}
                                    <MenuSection>
                                        <MenuItem
                                            onClick={this.props.onStartSelectingFileUpload}
                                        >
                                            {this.props.intl.formatMessage(sharedMessages.loadFromComputerTitle)}
                                        </MenuItem>
                                        <SB3Downloader
                                            showSaveFilePicker={this.props.showSaveFilePicker}
                                        >
                                            {(_className, downloadProject, extended) => (
                                                <React.Fragment>
                                                    {!this.props.isPlayerOnly && extended.available && (
                                                        <React.Fragment>
                                                            {extended.name !== null && (
                                                                // eslint-disable-next-line max-len
                                                                <MenuItem onClick={this.getSaveToComputerHandler(extended.saveToLastFile)}>
                                                                    <FormattedMessage
                                                                        defaultMessage="Save to {file}"
                                                                        // eslint-disable-next-line max-len
                                                                        description="Menu bar item to save project to an existing file on the user's computer"
                                                                        id="tw.saveTo"
                                                                        values={{
                                                                            file: extended.name
                                                                        }}
                                                                    />
                                                                </MenuItem>
                                                            )}
                                                            {/* eslint-disable-next-line max-len */}
                                                            <MenuItem onClick={this.getSaveToComputerHandler(extended.saveAsNew)}>
                                                                <FormattedMessage
                                                                    defaultMessage="Save as..."
                                                                    // eslint-disable-next-line max-len
                                                                    description="Menu bar item to select a new file to save the project as"
                                                                    id="tw.saveAs"
                                                                />
                                                            </MenuItem>
                                                        </React.Fragment>
                                                    )}
                                                    {!this.props.isPlayerOnly && notScratchDesktop() && (
                                                        <MenuItem
                                                            onClick={this.getSaveToComputerHandler(downloadProject)}
                                                        >
                                                            {extended.available ? (
                                                                <FormattedMessage
                                                                    defaultMessage="Save to separate file..."
                                                                    // eslint-disable-next-line max-len
                                                                    description="Download the project once, without being able to easily save to the same spot"
                                                                    id="tw.oldDownload"
                                                                />
                                                            ) : (
                                                                <FormattedMessage
                                                                    defaultMessage="Save to your computer"
                                                                    description="Menu bar item for downloading a project to your computer" // eslint-disable-line max-len
                                                                    id="gui.menuBar.downloadToComputer"
                                                                />
                                                            )}
                                                        </MenuItem>
                                                    )}
                                                </React.Fragment>
                                            )}
                                        </SB3Downloader>
                                    </MenuSection>
                                    {this.props.onClickPackager && (
                                        <MenuSection>
                                            <MenuItem
                                                onClick={this.handleClickPackager}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Package project"
                                                    // eslint-disable-next-line max-len
                                                    description="Menu bar item to open the current project in the packager"
                                                    id="tw.menuBar.package"
                                                />
                                            </MenuItem>
                                            <MenuItem
                                                // eslint-disable-next-line react/jsx-no-bind
                                                onClick={() => window.open('https://dashblocks.org/unpackager/', '_blank')}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Unpackager"
                                                    // eslint-disable-next-line max-len
                                                    description="Menu bar item to open the unpackager"
                                                    id="dash.menuBar.unpackage"
                                                />
                                            </MenuItem>
                                        </MenuSection>
                                    )}
                                    <MenuSection>
                                        <MenuItem onClick={this.handleClickRestorePoints}>
                                            <FormattedMessage
                                                defaultMessage="Restore points"
                                                description="Menu bar item to manage restore points"
                                                id="tw.menuBar.restorePoints"
                                            />
                                        </MenuItem>
                                    </MenuSection>
                                </MenuBarMenu>
                            </MenuLabel>
                        )}
                        <MenuLabel
                            open={this.props.editMenuOpen}
                            onOpen={this.props.onClickEdit}
                            onClose={this.props.onRequestCloseEdit}
                        >
                            <img
                                src={editIcon}
                                draggable={false}
                                width={20}
                                height={20}
                            />
                            <span className={styles.collapsibleLabel}>
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    description="Text for edit dropdown menu"
                                    id="gui.menuBar.edit"
                                />
                            </span>
                            <img
                                src={dropdownCaret}
                                draggable={false}
                                width={8}
                                height={5}
                            />
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.editMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                            >
                                {this.props.isPlayerOnly ? null : (
                                    <DeletionRestorer>{(handleRestore, {restorable, deletedItem}) => (
                                        <MenuItem
                                            className={classNames({[styles.disabled]: !restorable})}
                                            onClick={this.handleRestoreOption(handleRestore)}
                                        >
                                            {this.restoreOptionMessage(deletedItem)}
                                        </MenuItem>
                                    )}</DeletionRestorer>
                                )}
                                <MenuSection>
                                    <TurboMode>{(toggleTurboMode, {turboMode}) => (
                                        <MenuItem onClick={toggleTurboMode}>
                                            {turboMode ? (
                                                <FormattedMessage
                                                    defaultMessage="Turn off Turbo Mode"
                                                    description="Menu bar item for turning off turbo mode"
                                                    id="gui.menuBar.turboModeOff"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Turn on Turbo Mode"
                                                    description="Menu bar item for turning on turbo mode"
                                                    id="gui.menuBar.turboModeOn"
                                                />
                                            )}
                                        </MenuItem>
                                    )}</TurboMode>
                                    <FramerateChanger>{(changeFramerate, {framerate}) => (
                                        <MenuItem onClick={changeFramerate}>
                                            {framerate === 60 ? (
                                                <FormattedMessage
                                                    defaultMessage="Turn off 60 FPS Mode"
                                                    description="Menu bar item for turning off 60 FPS mode"
                                                    id="tw.menuBar.60off"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Turn on 60 FPS Mode"
                                                    description="Menu bar item for turning on 60 FPS mode"
                                                    id="tw.menuBar.60on"
                                                />
                                            )}
                                        </MenuItem>
                                    )}</FramerateChanger>
                                    <ChangeUsername>{changeUsername => (
                                        <MenuItem
                                            className={classNames({[styles.disabled]: !!this.props.session?.username})}
                                            onClick={changeUsername}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Change Username"
                                                description="Menu bar item for changing the username"
                                                id="tw.menuBar.changeUsername"
                                            />
                                        </MenuItem>
                                    )}</ChangeUsername>
                                    <CloudVariablesToggler>{(toggleCloudVariables, {enabled, canUseCloudVariables}) => (
                                        <MenuItem
                                            className={classNames({[styles.disabled]: !canUseCloudVariables})}
                                            onClick={toggleCloudVariables}
                                        >
                                            {canUseCloudVariables ? (
                                                enabled ? (
                                                    <FormattedMessage
                                                        defaultMessage="Disable Cloud Variables"
                                                        description="Menu bar item for disabling cloud variables"
                                                        id="tw.menuBar.cloudOff"
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        defaultMessage="Enable Cloud Variables"
                                                        description="Menu bar item for enabling cloud variables"
                                                        id="tw.menuBar.cloudOn"
                                                    />
                                                )
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Cloud Variables are not Available"
                                                    // eslint-disable-next-line max-len
                                                    description="Menu bar item for when cloud variables are not available"
                                                    id="tw.menuBar.cloudUnavailable"
                                                />
                                            )}
                                        </MenuItem>
                                    )}</CloudVariablesToggler>
                                    <MenuItem
                                        onClick={
                                            this.props.canManageCollaborators ?
                                            this.handleOpenCollaborators :
                                            // eslint-disable-next-line no-alert
                                            () => alert(this.props.intl.formatMessage(dashMessages.liveCollaborationUnavailable))
                                        }
                                        className={classNames({[styles.disabled]: !this.props.canManageCollaborators})}
                                    >
                                        {this.props.canManageCollaborators ? (
                                            <FormattedMessage
                                                id="dash.menuBar.liveCollaboration"
                                                defaultMessage="Live-collaboration"
                                                description="Open the project collaborators management dialog"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="dash.menuBar.liveCollaborationUnavailable"
                                                defaultMessage="Live-collaboration is not Available"
                                                description="Menu bar item for when live-collaboration is not available"
                                            />
                                        )}
                                    </MenuItem>
                                </MenuSection>
                                <MenuSection>
                                    <MenuItem onClick={this.props.onClickSettingsModal}>
                                        <FormattedMessage
                                            defaultMessage="Advanced Settings"
                                            description="Menu bar item for advanced settings"
                                            id="tw.menuBar.moreSettings"
                                        />
                                    </MenuItem>
                                </MenuSection>
                            </MenuBarMenu>
                        </MenuLabel>
                        {this.props.isTotallyNormal && (
                            <MenuLabel
                                open={this.props.modeMenuOpen}
                                onOpen={this.props.onClickMode}
                                onClose={this.props.onRequestCloseMode}
                            >
                                <FormattedMessage
                                    defaultMessage="Mode"
                                    description="Mode menu item in the menu bar"
                                    id="gui.menuBar.modeMenu"
                                />
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.modeMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                >
                                    <MenuSection>
                                        <MenuItem onClick={this.handleSetMode('NOW')}>
                                            <span className={classNames({[styles.inactive]: !this.props.modeNow})}>
                                                {'✓'}
                                            </span>
                                            {' '}
                                            <FormattedMessage
                                                defaultMessage="Normal mode"
                                                description="April fools: resets editor to not have any pranks"
                                                id="gui.menuBar.normalMode"
                                            />
                                        </MenuItem>
                                        <MenuItem onClick={this.handleSetMode('2020')}>
                                            <span className={classNames({[styles.inactive]: !this.props.mode2020})}>
                                                {'✓'}
                                            </span>
                                            {' '}
                                            <FormattedMessage
                                                defaultMessage="Caturday mode"
                                                description="April fools: Cat blocks mode"
                                                id="gui.menuBar.caturdayMode"
                                            />
                                        </MenuItem>
                                    </MenuSection>
                                </MenuBarMenu>
                            </MenuLabel>
                        )}

                        {this.props.onClickAddonSettings && (
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable)}
                                onClick={this.props.onClickAddonSettings}
                            >
                                <img
                                    src={addonsIcon}
                                    draggable={false}
                                    width={20}
                                    height={20}
                                />
                                <span className={styles.collapsibleLabel}>
                                    <FormattedMessage
                                        defaultMessage="Addons"
                                        description="Button to open addon settings"
                                        id="tw.menuBar.addons"
                                    />
                                </span>
                            </div>
                        )}
                        {this.props.onClickSettingsModal && (
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable)}
                                onClick={this.props.onClickSettingsModal}
                            >
                                <img
                                    src={advancedIcon}
                                    draggable={false}
                                    width={20}
                                    height={20}
                                />
                                <span className={styles.collapsibleLabel}>
                                    <FormattedMessage
                                        defaultMessage="Advanced"
                                        description="Button to open advanced settings menu"
                                        id="tw.menuBar.advanced"
                                    />
                                </span>
                            </div>
                        )}
                    </div>

                    <Divider className={styles.divider} />

                    {this.props.canEditTitle && !this.props.isPlayerOnly ? (
                        <div className={classNames(styles.menuBarItem, styles.growable)}>
                            <MenuBarItemTooltip
                                enable
                                id="title-field"
                            >
                                <ProjectTitleInput
                                    className={classNames(styles.titleFieldGrowable)}
                                />
                            </MenuBarItemTooltip>
                        </div>
                    ) : (this.props.authorUsername ? (
                        <AuthorInfo
                            className={styles.authorInfo}
                            avatarSrc={this.props.authorAvatarSrc}
                            frameId={this.props.authorAvatarFrame}
                            projectId={this.props.projectId}
                            projectTitle={this.props.projectTitle}
                            userId={this.props.authorId}
                            username={this.props.authorUsername}
                        />
                    ) : null)}
                    {this.props.canShare && this.props.canEditTitle ? (
                        (this.props.isShowingProject || this.props.isUpdating) && (
                            <div className={classNames(styles.menuBarItem)}>
                                <ProjectWatcher onDoneUpdating={this.props.onSeeCommunity}>
                                    {
                                        waitForUpdate => (
                                            <ShareButton
                                                className={styles.menuBarButton}
                                                isShared={this.props.isShared}
                                                isSharing={this.state.isSharing}
                                                /* eslint-disable react/jsx-no-bind, no-unused-expressions */
                                                onClick={() => {
                                                    this.props.isShared ?
                                                        this.handleClickUpdate(waitForUpdate) :
                                                        this.handleClickShare(waitForUpdate);
                                                }}
                                                /* eslint-enable react/jsx-no-bind */
                                            />
                                        )
                                    }
                                </ProjectWatcher>
                            </div>
                        )
                    ) : this.props.showComingSoon ? (
                        <div className={classNames(styles.menuBarItem)}>
                            <MenuBarItemTooltip id="share-button">
                                <ShareButton className={styles.menuBarButton} />
                            </MenuBarItemTooltip>
                        </div>
                    ) : null}
                    {this.props.canRemix && !this.props.isPlayerOnly && (
                        <div className={classNames(styles.menuBarItem)}>
                            {remixButton}
                        </div>
                    )}
                    <div className={classNames(styles.menuBarItem, styles.communityButtonWrapper)}>
                        {this.props.enableCommunity ? (
                            (!isScratchDesktop() && (this.props.isShowingProject || this.props.isUpdating)) && (
                                <ProjectWatcher onDoneUpdating={this.props.onSeeCommunity}>
                                    {
                                        waitForUpdate => (
                                            <CommunityButton
                                                className={styles.menuBarButton}
                                                /* eslint-disable react/jsx-no-bind */
                                                onClick={() => {
                                                    this.handleClickSeeCommunity(waitForUpdate);
                                                }}
                                                /* eslint-enable react/jsx-no-bind */
                                            />
                                        )
                                    }
                                </ProjectWatcher>
                            )
                        ) : (this.props.showComingSoon ? (
                            <MenuBarItemTooltip id="community-button">
                                <CommunityButton className={styles.menuBarButton} />
                            </MenuBarItemTooltip>
                        ) : (this.props.enableSeeInside ? (
                            <SeeInsideButton
                                className={styles.menuBarButton}
                                onClick={this.handleClickSeeInside}
                            />
                        ) : []))}
                    </div>
                    {!this.props.isPlayerOnly && !isScratchDesktop() && <div className={styles.menuBarItem}>
                        <a
                            className={styles.feedbackLink}
                            href="https://scratch.mit.edu/discuss/topic/879252/"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            {/* todo: icon */}
                            <Button className={styles.feedbackButton}>
                                <FormattedMessage
                                    defaultMessage="{APP_NAME} Forum"
                                    description="Button to give link to forum in the menu bar"
                                    id="dash.forumButton"
                                    values={{
                                        APP_NAME
                                    }}
                                />
                            </Button>
                        </a>
                    </div>}
                </div>
                <div className={styles.accountInfoGroup}>
                    {!this.props.isPlayerOnly && <div className={styles.menuBarItem}>
                        <TWSaveStatus
                            showSaveFilePicker={this.props.showSaveFilePicker}
                        />
                    </div>}
                    {this.props.sessionExists && this.props.session?.username ? (
                        // User is logged in
                        <React.Fragment>
                            <a href="messages">
                                <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable,
                                        styles.messagesButton
                                    )}
                                >
                                    <span
                                        className={
                                            this.props.session?.profile.unreadMessages > 0 ?
                                                styles.messagesCountVisible :
                                                styles.messagesCount
                                        }
                                    >{this.props.session?.profile.unreadMessages}</span>
                                    <img
                                        className={styles.messagesIcon}
                                        src={messagesIcon}
                                    />
                                </div>
                            </a>
                            <a href="mystuff">
                                <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable,
                                        styles.mystuffButton
                                    )}
                                >
                                    <img
                                        className={styles.mystuffIcon}
                                        src={mystuffIcon}
                                    />
                                </div>
                            </a>
                            <AccountNav
                                className={classNames(
                                    styles.menuBarItem,
                                    styles.hoverable,
                                    {[styles.active]: this.props.accountMenuOpen}
                                )}
                                isOpen={this.props.accountMenuOpen}
                                isRtl={this.props.isRtl}
                                menuBarMenuClassName={classNames(styles.menuBarMenu)}
                                onClick={this.props.onClickAccount}
                                onClose={this.props.onRequestCloseAccount}
                                onLogOut={this.handleClickLogOut}
                            />
                        </React.Fragment>
                    ) : (
                        // User not logged in
                        <React.Fragment>
                            <div
                                className={classNames(
                                    styles.menuBarItem,
                                    styles.hoverable
                                )}
                                key="join"
                                // eslint-disable-next-line react/jsx-no-bind
                                onMouseUp={() => window.open('./register', '_blank')}
                            >
                                <FormattedMessage
                                    defaultMessage="Join Dash"
                                    description="Link for creating a Dash account"
                                    id="dash.menuBar.joinDash"
                                />
                            </div>
                            <div
                                className={classNames(
                                    styles.menuBarItem,
                                    styles.hoverable
                                )}
                                key="login"
                                // eslint-disable-next-line react/jsx-no-bind
                                onMouseUp={() => window.open('./login', '_blank')}
                            >
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    description="Link for signing in to your Dash account"
                                    id="dash.menuBar.signIn"
                                />
                            </div>
                        </React.Fragment>
                    )}
                </div>

                {aboutButton}
            </Box>
        );

        return (
            <React.Fragment>
                {menuBar}
                {this.props.canManageCollaborators &&
                    this.state.collaboratorsProjectId === this.props.projectId && (
                    <CollaboratorsModal
                        key={this.props.projectId}
                        projectId={this.props.projectId}
                        isRtl={this.props.isRtl}
                        onClose={this.handleCloseCollaborators}
                    />
                )}
                {/* !process.env.OLD_COMPILER && (<TWNews item='dash:news1' id='new-compiler' />) */}
                {window.location.href.startsWith('https://dashblocks.org/scratch-gui') && (<TWNews
                    item="dash:news2"
                    id="dev-version"
                />)}
                {/* <TWNews item='dash:news3' id='new-year' /> */}
                {<TWNews
                    item="dash:news4"
                    id="support"
                />}
                {/* <TWNews
                    item="dash:news5"
                    id="collab-zippr"
                /> */}
            </React.Fragment>
        );
    }
}

MenuBar.propTypes = {
    enableSeeInside: PropTypes.bool,
    onClickSeeInside: PropTypes.func,
    aboutMenuOpen: PropTypes.bool,
    accountMenuOpen: PropTypes.bool,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorAvatarSrc: PropTypes.string,
    authorAvatarFrame: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageCollaborators: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.shape({
        sprite: PropTypes.string,
        error: PropTypes.string,
        id: PropTypes.number
    })),
    errorsMenuOpen: PropTypes.bool,
    onClickErrors: PropTypes.func,
    onRequestCloseErrors: PropTypes.func,
    confirmReadyToReplaceProject: PropTypes.func,
    editMenuOpen: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    handleSaveProject: PropTypes.func,
    intl: intlShape,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    isUpdating: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    mode2020: PropTypes.bool,
    modeMenuOpen: PropTypes.bool,
    modeNow: PropTypes.bool,
    onClickAbout: PropTypes.oneOfType([
        PropTypes.func, // button mode: call this callback when the About button is clicked
        PropTypes.arrayOf( // menu mode: list of items in the About menu
            PropTypes.shape({
                title: PropTypes.string, // text for the menu item
                onClick: PropTypes.func // call this callback when the menu item is clicked
            })
        )
    ]),
    onClickAccount: PropTypes.func,
    onClickAddonSettings: PropTypes.func,
    onClickDesktopSettings: PropTypes.func,
    onClickPackager: PropTypes.func,
    onClickRestorePoints: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickMode: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickNewWindow: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onClickSettings: PropTypes.func,
    onClickSettingsModal: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestCloseAbout: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseEdit: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseMode: PropTypes.func,
    onRequestCloseSettings: PropTypes.func,
    onRequestOpenAbout: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onSetTimeTravelMode: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    projectId: PropTypes.string,
    projectTitle: PropTypes.string,
    session: PropTypes.object,
    sessionExists: PropTypes.bool,
    settingsMenuOpen: PropTypes.bool,
    shouldSaveBeforeTransition: PropTypes.func,
    showSaveFilePicker: PropTypes.func,
    showComingSoon: PropTypes.bool,
    setSession: PropTypes.func,
    username: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired
};

MenuBar.defaultProps = {
    onShare: () => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const session = state.scratchGui.dash.session;
    const projectId = state.scratchGui.projectState.projectId;
    const isScratchProject = projectId && projectId.startsWith('s');
    const userOwnsProject = (
        !isScratchProject && ((
            state.scratchGui.tw.author &&
            state.scratchGui.tw.author.userId &&
            session &&
            state.scratchGui.tw.author.userId === session.id
        ) || (
            session &&
            session.role === 'dashteam'
        ))
    );
    return {
        authorUsername: state.scratchGui.tw.author.username,
        authorId: state.scratchGui.tw.author.userId,
        authorAvatarSrc: state.scratchGui.tw.author.thumbnail,
        authorAvatarFrame: state.scratchGui.tw.author.frameId,
        projectId,
        canManageCollaborators: /^[1-9]\d{0,19}$/.test(projectId) && Boolean(
            session && state.scratchGui.tw.author && state.scratchGui.tw.author.userId &&
            String(state.scratchGui.tw.author.userId) === String(session.id)
        ),
        aboutMenuOpen: aboutMenuOpen(state),
        accountMenuOpen: accountMenuOpen(state),
        currentLocale: state.locales.locale,
        fileMenuOpen: fileMenuOpen(state),
        editMenuOpen: editMenuOpen(state),
        errors: state.scratchGui.tw.compileErrors,
        errorsMenuOpen: errorsMenuOpen(state),
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        isShared: projectId !== '0' && userOwnsProject,
        canEditTitle: projectId === '0' || userOwnsProject,
        canRemix: projectId !== '0' && !isScratchProject,
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        modeMenuOpen: modeMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        sessionExists: session !== null,
        settingsMenuOpen: settingsMenuOpen(state),
        session: session || null,
        userOwnsProject,
        vm: state.scratchGui.vm,
        mode220022BC: isTimeTravel220022BC(state),
        mode1920: isTimeTravel1920(state),
        mode1990: isTimeTravel1990(state),
        mode2020: isTimeTravel2020(state),
        modeNow: isTimeTravelNow(state)
    };
};

const mapDispatchToProps = dispatch => ({
    onClickSeeInside: () => dispatch(setPlayer(false)),
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onClickAccount: () => dispatch(openAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(openFileMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onClickEdit: () => dispatch(openEditMenu()),
    onRequestCloseEdit: () => dispatch(closeEditMenu()),
    onClickErrors: () => dispatch(openErrorsMenu()),
    onRequestCloseErrors: () => dispatch(closeErrorsMenu()),
    onClickLogin: () => dispatch(openLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onClickMode: () => dispatch(openModeMenu()),
    onRequestCloseMode: () => dispatch(closeModeMenu()),
    onRequestOpenAbout: () => dispatch(openAboutMenu()),
    onRequestCloseAbout: () => dispatch(closeAboutMenu()),
    onClickRestorePoints: () => dispatch(openRestorePointModal()),
    onClickSettings: () => dispatch(openSettingsMenu()),
    onClickSettingsModal: () => {
        dispatch(closeEditMenu());
        dispatch(openSettingsModal());
    },
    onRequestCloseSettings: () => dispatch(closeSettingsMenu()),
    onClickNew: needSave => {
        dispatch(requestNewProject(needSave));
        dispatch(setFileHandle(null));
    },
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    onSetTimeTravelMode: mode => dispatch(setTimeTravel(mode)),
    setSession: session => dispatch(setSession(session))
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(MenuBar);
